import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  HostListener,
  AfterViewInit,
  TemplateRef,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { Subscription, forkJoin } from "rxjs";
import { Router, RouterLink } from "@angular/router";
import { formatNumber, NgClass, NgStyle } from "@angular/common";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { Options } from "highcharts";
import { HomeControllerService } from "../../../services/server/HomeController.service";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { MapSizeService } from "../../../modules/map-module/map-services/MapSizeService.service";
import {
  DEVICE_BY_METROLOGY,
  DeviceTypeService
} from "../../../services/shared/DeviceTypeService.service";
import { MapDeviceMinimalParseService } from "../../../modules/map-module/map-services/MapDeviceMinimalParseService.service";
import { TemplateService } from "../../../services/shared/TemplateService.service";
import { HomeService } from "./home.service";
import { ManufacturerService } from "../../../services/shared/ManufacturerService.service";
import { DateParserService } from "../../../services/shared/DateParserService.service";
import { DataAnalysisControllerService } from "../../../services/server/DataAnalysisController.service";
import { NetworkStateControllerService } from "../../../services/server/NetworkStateController.service";
import { SuspicionsService } from "../../../services/shared/SuspicionsService.service";
import { GraphOptionsService } from "../../../modules/graph-module/GraphOptionsService.service";
import { ToastService } from "../../../services/shared/ToastService.service";
import { Agrupation } from "../../../interfaces/AgrupationGlobalInterface.type";
import { DeviceTypes } from "./HomeInterface.type";
import { METROLOGY_TYPE, MapDevice } from "../../../interfaces/DeviceGlobalInterface.type";
import { GatewayLocation, MapGateway } from "../../../interfaces/GatewayGlobalInterface.type";
import { MapSearch } from "../../../modules/map-module/MapInterface.type";
import { THEME_COLORS } from "../../../themes/THEME_COLORS";
import { MapControllerComponent } from "../../../modules/map-module/map-controller/map-controller.component";
import { GRAPH_CONFIG } from "../../../modules/graph-module/GRAPH_CONFIG";
import { PROFILES } from "../../../../assets/profiles/profiles";
import { DEVICE_BY_COMM } from "../../../services/shared/DeviceTypeService.service";
import { GRAPH_TYPES } from "../devices/devices-common-components/device-consumption-graph/device-consumption-graph.component";
import { MAP_LAYERS, MAP_TYPES_NAMES } from "../../../modules/map-module/map-variables/MAP_TYPES";
import { KpisCardsData } from "../network-state/NetworkStateInterface.type";
import { MANUFACTURER_INDEX } from "../../../../assets/manufacturers/MANUFACTURER_INDEX";
import { MatButtonModule } from "@angular/material/button";
import { GraphControllerComponent } from "../../../modules/graph-module/graph-controller/graph-controller.component";

/**
 * Shows the active agrupation's device map, status cards, alarms, and consumption summaries.
 * ARSON profiles also load gateway locations for the active entity.
 */
@Component({
  selector: "app-main",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    GraphControllerComponent,
    NgStyle,
    RouterLink,
    MatButtonModule,
    MapControllerComponent,
    TranslateModule
  ]
})
export class HomeComponent implements OnInit, AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  elseBlock: TemplateRef<any> | null = this.TemplateService.get("elseBlock");

  // Session data stream
  sessionProfile: string;
  currentAgrupation: Agrupation;
  isGas: boolean;
  readonly PROFILES = PROFILES;
  formatNumber = formatNumber;
  readonly DEVICE_BY_COMM = DEVICE_BY_COMM;
  readonly DEVICE_BY_METROLOGY = DEVICE_BY_METROLOGY;
  readonly ALL_EXCEPT_CONCENTRATORS = Object.values(DEVICE_BY_METROLOGY).filter(
    m =>
      m !== DEVICE_BY_METROLOGY.LW_MBUS &&
      m !== DEVICE_BY_METROLOGY.LW_UNE &&
      m !== DEVICE_BY_METROLOGY.CONCENTRATOR
  );
  currentColors: object;
  numberFormat = untracked(() => this.SessionDataService.numberFormat());

  // Device and gateway arrays
  devices: MapDevice[];
  devicesTotal: number = 0;
  gateways: MapGateway[];
  gatewayLocations: GatewayLocation[];
  deviceModels: { manufacturer: number; model: number }[] = [];
  // "manufacturer:model" keys already in deviceModels, for O(1) duplicate checks
  // instead of scanning deviceModels (.some()) per device
  private deviceModelsSeen: Set<string> = new Set();
  deviceModelsFilter: {
    modelList: { manufacturer: number; model: number }[];
    manufacturerAttribute: string;
    modelAttribute: string;
  };
  totalInNetwork: number;
  totalPending: number;
  totalNoCommunication: number;
  totalGraphHiddenSeries: string[];

  // HTML column refs
  @ViewChild("homeData") homeData: ElementRef;

  // Consumption
  lastMonthConsumption: {
    value: number;
    timestamp: number;
    valueParsed: string;
    date: string;
  };
  lastDayConsumption: {
    value: number;
    timestamp: number;
    valueParsed: string;
    date: string;
  };

  // KPIs
  KpisCardsData: KpisCardsData;

  // Alarms
  last5DaysAlarms: number;
  currentAlarms: number;
  alarmCodeList: { alarmCode: number | string; total: number; name: string }[];

  // Map
  mapFilter: string = "main";
  mapType: string = MAP_TYPES_NAMES.STANDARD;
  @ViewChild("homePanel") homePanel: ElementRef;
  @ViewChild("mapContainer") mapContainer: ElementRef;
  mapHeight: number;
  mapPadding: number = 0;
  mapSearch: MapSearch;
  @ViewChild(MapControllerComponent) mapController: MapControllerComponent;
  drawAgrupationOutline: boolean;
  mapActiveLayers: string[];

  // Device types
  devicesCountByType: DeviceTypes = new DeviceTypes();

  // Weather API
  weatherCoords: { latitude: number; longitude: number };

  @HostListener("window:resize")
  onResize() {
    this.resizeMap();
  }

  // Charts
  graphsHidden: boolean = false;
  deviceTotalsHighchartsOptions: any;
  deviceTotalsChartOptions: any;
  alarmsHighchartsOptions: Options;
  alarmsChartOptions: any;
  alarmsCloudHighchartsOptions: Options;
  alarmsCloudChartOptions: any;
  consumptionHighchartsOptions: Options;
  consumptionChartOptions: any;
  consumptionSeries: any;
  @ViewChild("homeDataPanel") homeDataPanel: ElementRef;
  deviceTypeHighchartsOptions: Options;
  networkHighchartsOptions: Options;
  devicesChartOptions: any[];
  devicesChartObjects: any[] = [];
  networkChartOptions: any[];
  networkChartObjects: any[] = [];
  globalChartObject: any;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly HomeController: HomeControllerService,
    private readonly ManufacturerService: ManufacturerService,
    private readonly MapDeviceMinimalParse: MapDeviceMinimalParseService,
    private readonly DeviceTypeService: DeviceTypeService,
    private readonly GraphOptionsService: GraphOptionsService,
    public HomeService: HomeService,
    private readonly MapSizeService: MapSizeService,
    private readonly NetworkStateController: NetworkStateControllerService,
    private readonly DataAnalysisController: DataAnalysisControllerService,
    private readonly DateParser: DateParserService,
    private readonly router: Router,
    private readonly SessionDataService: SessionDataService,
    private readonly SuspicionsService: SuspicionsService,
    private readonly TemplateService: TemplateService,
    private readonly ToastService: ToastService,
    private readonly translate: TranslateService
  ) {}

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.sessionProfile = untracked(() => this.SessionDataService.profile());
    this.currentAgrupation = untracked(() => this.SessionDataService.agrupation());
    this.drawAgrupationOutline = this.currentAgrupation?.virtual;
    this.currentColors = untracked(() => this.SessionDataService.colors());

    if (this.currentAgrupation) {
      this.loadComponent();
    }
  }

  /***************************************************************************/
  // ANCHOR After-render execution
  /***************************************************************************/
  ngAfterViewInit(): void {
    if (this.currentAgrupation) {
      // Wait until the map container is laid out before measuring it.
      setTimeout(() => this.resizeMap(), 0);
    }
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  loadComponent(): void {
    this.isGas =
      this.sessionProfile === PROFILES.ARSON || this.sessionProfile === PROFILES.USUARIO_ARSON
        ? untracked(() => this.SessionDataService.client()).gas
        : untracked(() => this.SessionDataService.entity()).gasClient;
    this.devices = null;
    this.gateways = null;
    this.getCardsData();
    this.getMapData();
    // Consumption chart and KPIs do not depend on map data: requested in parallel
    // instead of waiting for the map response to be processed
    this.setConsumptionHighchartsOptions();
    this.getConsumptionGraph();
    this.loadKpisData();
  }

  getCardsData(): void {
    this.HomeController.getCards(this.currentAgrupation.id).subscribe(response => {
      if (response["code"] === 0) {
        this.last5DaysAlarms = response["body"]?.nroAlarmsLastFiveDays;
        this.lastMonthConsumption = {
          value: this.isGas
            ? response["body"]?.lastMonthConsumptionNm
            : response["body"]?.lastMonthConsumption,
          timestamp: response["body"]?.lastMonthConsumptionTimestamp,
          valueParsed:
            formatNumber(
              this.isGas
                ? response["body"]?.lastMonthConsumptionNm
                : response["body"]?.lastMonthConsumption,
              this.numberFormat
            ) + (this.isGas ? " Nm³" : " m³"),
          date: this.DateParser.parseDate(
            response["body"]?.lastMonthConsumptionTimestamp,
            untracked(() => this.SessionDataService.dateFormat())
          )
        };
        this.lastDayConsumption = {
          value: this.isGas
            ? response["body"]?.lastDayConsumptionNm
            : response["body"]?.lastDayConsumption,
          timestamp: response["body"]?.lastDayConsumptionTimestamp,
          valueParsed:
            formatNumber(
              this.isGas
                ? response["body"]?.lastDayConsumptionNm
                : response["body"]?.lastDayConsumption,
              this.numberFormat
            ) + (this.isGas ? " Nm³" : " m³"),
          date: this.DateParser.parseDate(
            response["body"]?.lastDayConsumptionTimestamp,
            untracked(() => this.SessionDataService.dateFormat())
          )
        };
      }
    });
  }

  setDeviceTotalsHighchartsOptions(): void {
    const self = this;
    let highchartsOptions = {
      exporting: { enabled: false },
      tooltip: { enabled: false },
      credits: { enabled: false },
      accessibility: { enabled: false },
      title: { text: "" },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: false
          }
        },
        bar: {
          stacking: "percent",
          borderWidth: 0.5,
          borderColor: "black",
          animation: false,
          pointWidth: 20,
          dataLabels: { enabled: false },
          point: {
            events: {
              mouseOver: e => {
                let serie = e.target.series.userOptions;
                if (
                  serie.device !== "gateways" &&
                  serie.device !== "concentrators" &&
                  serie.device !== "txn"
                ) {
                  this.highlightLinkedSeries(serie.id, serie.device);
                }
              },
              mouseOut: e => {
                let serie = e.target.series.userOptions;
                if (
                  serie.device !== "gateways" &&
                  serie.device !== "concentrators" &&
                  serie.device !== "txn"
                ) {
                  this.highlightLinkedSeries();
                }
              },
              click: function (e) {
                if (e.altKey) {
                  self.updateMapVisibility(
                    this.series.userOptions.device,
                    this.series.userOptions.id
                  );
                } else if (e.ctrlKey) {
                  self.updateMapVisibility(
                    this.series.userOptions.device,
                    this.series.userOptions.id,
                    true
                  );
                } else {
                  self.HomeService.getLinkDeviceType(
                    e.point.series.userOptions.device,
                    e.point.series.userOptions.id
                  );
                }
              }
            }
          }
        }
      }
    };
    this.deviceTotalsHighchartsOptions = highchartsOptions;
  }

  highlightLinkedSeries(serie?: string, device?: string): void {
    if (serie === "no-communication" && device === "externalWithoutCom") {
      serie = "without-communication";
    }
    switch (serie) {
      case "in-network":
        this.totalGraphHiddenSeries = ["no-communication", "pending", "without-communication"];
        this.updateSerieState("in-network");
        break;
      case "no-communication":
        this.totalGraphHiddenSeries = ["pending", "in-network", "without-communication"];
        this.updateSerieState("no-communication");
        break;
      case "pending":
        this.totalGraphHiddenSeries = ["no-communication", "in-network", "without-communication"];
        this.updateSerieState("pending");
        break;
      case "without-communication":
        this.totalGraphHiddenSeries = ["no-communication", "pending", "in-network"];
        this.updateSerieState("without-communication");
        break;
      default:
        this.totalGraphHiddenSeries = [];
        this.updateSerieState();
        break;
    }
  }

  highlightLinkedGlobalSeries(serie?: string): void {
    this.globalChartObject?.series?.forEach(chartSerie => {
      if (
        (chartSerie.userOptions.id === serie &&
          chartSerie.userOptions.device !== "externalWithoutCom") ||
        (serie === "without-communication" &&
          chartSerie.userOptions.device === "externalWithoutCom")
      ) {
        chartSerie.setState("normal");
      } else if (serie) {
        chartSerie.setState("inactive");
      } else {
        chartSerie.setState("normal");
      }
    });
  }

  updateSerieState(serie?: string): void {
    // Devices
    this.devicesChartObjects?.forEach(chart => {
      chart.series?.forEach(chartSerie => {
        if (
          (chartSerie.userOptions.id === serie &&
            chartSerie.userOptions.device !== "externalWithoutCom") ||
          (serie === "without-communication" &&
            chartSerie.userOptions.device === "externalWithoutCom")
        ) {
          chartSerie.setState("normal");
        } else if (serie) {
          chartSerie.setState("inactive");
        } else {
          chartSerie.setState("normal");
        }
      });
    });

    // Network
    this.networkChartObjects?.forEach(chart => {
      chart.series?.forEach(chartSerie => {
        if (serie) {
          chartSerie.setState("inactive");
        } else {
          chartSerie.setState("normal");
        }
      });
    });

    // Global
    this.highlightLinkedGlobalSeries(serie);
  }

  // Fetch map data
  getMapData(): void {
    let gateways = [];
    let devices = [];

    let requests = [
      // Map
      this.HomeController.getMarkers(this.currentAgrupation.id)
    ];

    if (this.sessionProfile === PROFILES.ARSON || this.sessionProfile === PROFILES.USUARIO_ARSON) {
      requests.push(
        // Locations
        this.HomeController.getLocations(untracked(() => this.SessionDataService.entity())?.id)
      );
    }

    forkJoin(requests).subscribe(responses => {
      // Locations response
      if (responses[1] && responses[1]["code"] === 0) {
        this.gatewayLocations = responses[1]["body"];
      }

      // Map response
      if (responses[0]["code"] === 0) {
        let nroMetersWithAlarm: number = 0;
        let nroTxn1: number = 0;
        let nroTxn4: number = 0;
        let alarmCodeList = [];
        // alarmCode -> alarmCodeList entry index, avoids scanning alarmCodeList (.find())
        // for each alarm code of each device
        const alarmCodeListIndex = new Map<
          number,
          { alarmCode: number; total: number; name: string }
        >();

        // Devices
        devices = this.MapDeviceMinimalParse.parseDevices(responses[0]["body"]["contadores"]);
        // Filter out test devices
        devices = devices.filter(
          (device: MapDevice) => device.fabricante !== MANUFACTURER_INDEX.ARSON
        );
        // Filter devices of agrupations hidden in virtual view
        if (this.currentAgrupation.virtual) {
          devices = this.HomeService.filterDisabledAgrupations(devices);
        }
        // Gateways
        gateways = responses[0]["body"]["gateways"];
        // Card data
        devices?.forEach((device: MapDevice) => {
          // Manufacturer/model: Set instead of scanning deviceModels (.some()) per device;
          // with many devices and models, the O(n) check per device made it O(n×m)
          const deviceModelKey = device.fabricante + ":" + device.devType;
          if (!this.deviceModelsSeen.has(deviceModelKey)) {
            this.deviceModelsSeen.add(deviceModelKey);
            this.deviceModels.push({
              manufacturer: device.fabricante,
              model: device.devType
            });
          }

          const deviceType = this.DeviceTypeService.getDeviceTypeByMask(
            device.tipo,
            device.metrologyType,
            device.fabricante,
            device.devType
          );

          const isSensor =
            device.metrologyType === METROLOGY_TYPE.SENSOR ||
            device.metrologyType === METROLOGY_TYPE.ACOUSTIC_SENSOR ||
            device.metrologyType === METROLOGY_TYPE.DISTANCE_SENSOR ||
            device.metrologyType === METROLOGY_TYPE.PRESSURE_SENSOR ||
            device.metrologyType === METROLOGY_TYPE.VALVE_STATUS_SENSOR;

          // ANCHOR TXN
          if (deviceType === DEVICE_BY_COMM.TXN) {
            let txnType = this.ManufacturerService.getManufacturer(
              device.fabricante,
              device.devType
            )?.deviceText;
            if (txnType === "N1") {
              nroTxn1++;
            }
            if (txnType === "N4") {
              nroTxn4++;
            }
          } else if (
            device.metrologyType !== METROLOGY_TYPE.MBUS_CONCENTRATOR &&
            device.metrologyType !== METROLOGY_TYPE.UNE_CONCENTRATOR
          ) {
            this.devicesTotal++;
          }

          // ANCHOR LW meters
          if (
            (deviceType === DEVICE_BY_COMM.LW || deviceType === DEVICE_BY_COMM.LW_MBUS) &&
            (device.metrologyType === METROLOGY_TYPE.WATER ||
              device.metrologyType === METROLOGY_TYPE.GAS)
          ) {
            if (!device.walkBy) this.devicesCountByType.meters.total++;
            if (device.walkBy) {
              this.devicesCountByType.walkBy.total++;
              this.devicesCountByType.walkBy.walkBy++;
            } else if (device.unidadVentaGw && !device.walkBy) {
              if (device.comunica) {
                this.devicesCountByType.meters.com++;
              } else {
                this.devicesCountByType.meters.noCom++;
              }
            } else if (!device.unidadVentaGw && !device.walkBy) {
              this.devicesCountByType.meters.noAssigned++;
            }
          }

          // ANCHOR Concentrators
          const isTpeConcentrator =
            device.metrologyType === METROLOGY_TYPE.MBUS_CONCENTRATOR &&
            this.DeviceTypeService.isTpeNetmore(device.tipo);
          if (
            deviceType != DEVICE_BY_COMM.TXN &&
            (device.metrologyType === METROLOGY_TYPE.UNE_CONCENTRATOR ||
              (device.metrologyType === METROLOGY_TYPE.MBUS_CONCENTRATOR && !isTpeConcentrator))
          ) {
            this.devicesCountByType.concentrators.total++;

            if (device.unidadVentaGw) {
              if (device.comunica) {
                this.devicesCountByType.concentrators.com++;
              } else {
                this.devicesCountByType.concentrators.noCom++;
              }
            } else if (!device.unidadVentaGw) {
              this.devicesCountByType.concentrators.noAssigned++;
            }
          }

          // ANCHOR TPE Netmore concentrators (LW_MBUS_CON with bit 13)
          if (isTpeConcentrator) {
            this.devicesCountByType.tpeNetmore.total++;
            if (device.comunica) {
              this.devicesCountByType.tpeNetmore.com++;
            } else {
              this.devicesCountByType.tpeNetmore.noCom++;
            }
          }

          // ANCHOR Valves
          if (device.metrologyType === METROLOGY_TYPE.WATER_VALVE) {
            this.devicesCountByType.valves.total++;
            if (device.unidadVentaGw) {
              if (device.comunica) {
                this.devicesCountByType.valves.com++;
              } else {
                this.devicesCountByType.valves.noCom++;
              }
            } else {
              this.devicesCountByType.valves.noAssigned++;
            }
          }

          // ANCHOR MBus meters
          if (
            (device.metrologyType === METROLOGY_TYPE.WATER ||
              device.metrologyType === METROLOGY_TYPE.GAS) &&
            deviceType === DEVICE_BY_COMM.MBUS
          ) {
            if (!device.walkBy) this.devicesCountByType.mbus.total++;
            if (device.walkBy) {
              this.devicesCountByType.walkBy.total++;
              this.devicesCountByType.walkBy.walkBy++;
            } else if (device.comunica) {
              this.devicesCountByType.mbus.com++;
            } else {
              this.devicesCountByType.mbus.noCom++;
            }
          }

          // ANCHOR Wavenis meters
          if (
            (device.metrologyType === METROLOGY_TYPE.WATER ||
              device.metrologyType === METROLOGY_TYPE.GAS) &&
            deviceType === DEVICE_BY_COMM.WAVENIS
          ) {
            this.devicesCountByType.wavenis.total++;
            this.devicesCountByType.wavenis.noCom++;
          }

          // ANCHOR UNE meters
          if (deviceType === DEVICE_BY_COMM.UNE) {
            this.devicesCountByType.une.total++;
            if (!device.comunica) {
              this.devicesCountByType.une.noCom++;
            }
          }

          // ANCHOR External meters
          if (
            !isSensor &&
            (deviceType === DEVICE_BY_COMM.EK280 ||
              deviceType === DEVICE_BY_COMM.OWASYS ||
              deviceType === DEVICE_BY_COMM.PLUM ||
              deviceType === DEVICE_BY_COMM.EXTERNO ||
              deviceType === DEVICE_BY_COMM.ERM ||
              deviceType === DEVICE_BY_COMM.API ||
              deviceType === DEVICE_BY_COMM.API_TOKEN ||
              deviceType === DEVICE_BY_COMM.API_TOKEN)
          ) {
            this.devicesCountByType.external.total++;
            if (device.comunica) {
              this.devicesCountByType.external.com++;
            } else {
              this.devicesCountByType.external.noCom++;
            }
          }

          // ANCHOR NBIOT UDP meters
          if (!isSensor && deviceType === DEVICE_BY_COMM.NBIOT_UDP) {
            this.devicesCountByType.nbiotUdp.total++;
            if (device.comunica) {
              this.devicesCountByType.nbiotUdp.com++;
            } else {
              this.devicesCountByType.nbiotUdp.noCom++;
            }
          }

          // ANCHOR External meters without communication
          if (deviceType === DEVICE_BY_COMM.NO_LORA_NO_COM) {
            this.devicesCountByType.externalWithoutCom.total++;
            this.devicesCountByType.externalWithoutCom.noCom++;
          }

          // ANCHOR NBIoT meters
          if (!isSensor && deviceType === DEVICE_BY_COMM.NBIOT) {
            this.devicesCountByType.nbiot.total++;
            if (device.comunica) {
              this.devicesCountByType.nbiot.com++;
            } else {
              this.devicesCountByType.nbiot.noCom++;
            }
          }

          // ANCHOR LNS Netmore
          if (deviceType === DEVICE_BY_COMM.LNS_NETMORE) {
            this.devicesCountByType.lnsNetmore.total++;
            if (device.comunica) {
              this.devicesCountByType.lnsNetmore.com++;
            } else if (!device.lastCommunication) {
              this.devicesCountByType.lnsNetmore.noAssigned++;
            } else {
              this.devicesCountByType.lnsNetmore.noCom++;
            }
          }

          // ANCHOR TPE Netmore
          if (deviceType === DEVICE_BY_COMM.TPE_NETMORE) {
            this.devicesCountByType.tpeNetmore.total++;
            if (device.comunica) {
              this.devicesCountByType.tpeNetmore.com++;
            } else if (!device.lastCommunication) {
              this.devicesCountByType.tpeNetmore.noAssigned++;
            } else {
              this.devicesCountByType.tpeNetmore.noCom++;
            }
          }

          // ANCHOR Pressure sensors
          // Excludes devices already counted as external/NBIoT (see "External meters" and
          // NBIoT/NBIoT UDP/LNS/TPE Netmore blocks above) to avoid double counting
          const isExternalCommDeviceType =
            deviceType != DEVICE_BY_COMM.LNS_NETMORE &&
            deviceType != DEVICE_BY_COMM.TPE_NETMORE &&
            (deviceType === DEVICE_BY_COMM.EK280 ||
              deviceType === DEVICE_BY_COMM.OWASYS ||
              deviceType === DEVICE_BY_COMM.PLUM ||
              deviceType === DEVICE_BY_COMM.EXTERNO ||
              deviceType === DEVICE_BY_COMM.ERM ||
              deviceType === DEVICE_BY_COMM.API ||
              deviceType === DEVICE_BY_COMM.API_TOKEN ||
              deviceType === DEVICE_BY_COMM.NBIOT ||
              deviceType === DEVICE_BY_COMM.NBIOT_UDP);

          // ANCHOR Sensors
          if (isSensor && !isExternalCommDeviceType) {
            this.devicesCountByType.sensors.total++;
            if (device.unidadVentaGw) {
              if (device.comunica) {
                this.devicesCountByType.sensors.com++;
              } else {
                this.devicesCountByType.sensors.noCom++;
              }
            } else if (!device.unidadVentaGw) {
              this.devicesCountByType.sensors.noAssigned++;
            }
          }

          // ANCHOR External sensors
          if (isSensor && isExternalCommDeviceType) {
            this.devicesCountByType.externalSensors.total++;
            if (device.comunica) {
              this.devicesCountByType.externalSensors.com++;
            } else {
              this.devicesCountByType.externalSensors.noCom++;
            }
          }

          // ANCHOR Satellites
          if (device.metrologyType === METROLOGY_TYPE.SATELITE && device.unidadVentaGw) {
            this.devicesCountByType.satelites.total++;
            if (device.comunica) {
              this.devicesCountByType.satelites.com++;
            } else {
              this.devicesCountByType.satelites.noCom++;
            }
          }

          // With alarms
          if (device.alarm) {
            nroMetersWithAlarm++;

            if (device.alarmCodes) {
              device.alarmCodes?.forEach(alarmCode => {
                let addedAlarm = alarmCodeListIndex.get(alarmCode);
                if (addedAlarm) {
                  addedAlarm.total++;
                } else {
                  addedAlarm = {
                    alarmCode: alarmCode,
                    total: 1,
                    name: this.translate.instant("_alert_meter." + alarmCode)
                  };
                  alarmCodeListIndex.set(alarmCode, addedAlarm);
                  alarmCodeList.push(addedAlarm);
                }
              });
            }
          }
        });

        // ANCHOR TXN devices
        if (nroTxn1 > 0 || nroTxn4 > 0) {
          this.devicesCountByType.txn.total = nroTxn1 + nroTxn4;
          this.devicesCountByType.txn.txn1 = nroTxn1;
          this.devicesCountByType.txn.txn4 = nroTxn4;
        }

        // ANCHOR Gateways
        gateways?.forEach(gateway => {
          this.devicesCountByType.gateways.total++;
          if (!gateway.contadoresPrincipal && !gateway.contadoresRedundante) {
            this.devicesCountByType.gateways.noDevice++;
          } else if (gateway.comunica) {
            this.devicesCountByType.gateways.com++;
          } else {
            this.devicesCountByType.gateways.noCom++;
          }
        });

        // With alarms
        this.currentAlarms = nroMetersWithAlarm;
        this.alarmCodeList = untracked(() => this.SessionDataService.suspicionActive())
          ? alarmCodeList
          : this.SuspicionsService.filterSuspicions(alarmCodeList);
      }

      this.mapSearch = {
        title: this.translate.instant("search"),
        bindValue: "id",
        bindLabel: "nroSerie",
        data: devices
      };

      this.devices = devices;
      this.gateways = gateways;

      if (this.devicesTotal !== 0) {
        this.setDeviceTotalsHighchartsOptions();
        let totalChart = this.HomeService.setDeviceTotalsChartsOptions(
          this.devicesCountByType,
          this.homeDataPanel
        );
        this.totalPending = totalChart.total.pending;
        this.totalInNetwork = totalChart.total.com;
        this.totalNoCommunication = totalChart.total.noCom;
        this.deviceTotalsChartOptions = totalChart.chartOptions;
        this.deviceTypeHighchartsOptions = this.HomeService.setDeviceTypesHighchartsOptions();
        this.devicesChartOptions = this.HomeService.setDevicesChartsOptions(
          this.devicesCountByType
        );
        this.alarmsHighchartsOptions = this.HomeService.setAlarmsHighchartsOptions();
        this.alarmsChartOptions = this.HomeService.setAlarmsChartsOptions(
          this.devicesTotal,
          this.currentAlarms
        );
        if (this.alarmCodeList?.length > 0) {
          this.alarmsCloudHighchartsOptions = this.HomeService.setAlarmsCloudHighchartsOptions();
          this.alarmsCloudChartOptions = this.HomeService.setAlarmsCloudChartsOptions(
            this.alarmCodeList
          );
        }
      }

      if (this.devicesTotal !== 0 || this.gateways?.length > 0) {
        this.networkHighchartsOptions = this.HomeService.setDeviceTypesHighchartsOptions();
        this.networkChartOptions = this.HomeService.setDevicesChartsOptions(
          this.devicesCountByType,
          true
        );
      }

      this.getWeatherCoords();

      this.deviceModelsFilter = {
        modelList: this.deviceModels,
        manufacturerAttribute: "fabricante",
        modelAttribute: "devType"
      };
    });
  }

  // Fetch coordinates for the weather API
  getWeatherCoords(): void {
    // Average coordinates in one pass (previously 5 full scans of devices)
    let latitudeSum = 0;
    let longitudeSum = 0;
    let count = 0;
    this.devices.forEach((device: MapDevice) => {
      if (device.latitude != null && device.longitude != null) {
        latitudeSum += parseFloat(String(device.latitude));
        longitudeSum += parseFloat(String(device.longitude));
        count++;
      }
    });

    if (count > 0) {
      this.SessionDataService.sendWeatherCoords({
        latitude: latitudeSum / count,
        longitude: longitudeSum / count,
        agrupation: this.currentAgrupation.name
      });
    } else {
      this.SessionDataService.sendWeatherCoords(null);
    }
  }

  // Resize map to match card visibility
  resizeMap(): void {
    this.mapHeight =
      window.innerWidth > 980
        ? this.MapSizeService.calcMapHeight(this.homePanel, null, this.mapPadding)
        : 600;
  }

  getConsumptionGraph(): void {
    this.DataAnalysisController.getAgrupationConsumption(
      this.currentAgrupation.id,
      this.isGas ? GRAPH_TYPES.NORMALIZED_CONSUMPTION : GRAPH_TYPES.CONSUMPTION,
      this.DateParser.getLastDays(8).startDate.valueOf(),
      this.DateParser.getLastDays(1).startDate.valueOf()
    ).subscribe(response => {
      if (response["code"] === 0) {
        this.consumptionSeries = [
          {
            id: "total-consumption",
            type: "area",
            name: this.translate.instant("total-consumption"),
            data: response["body"],
            dataGrouping: { approximation: "sum" },
            tooltip: {
              valueSuffix: this.isGas ? " Nm³" : " m³",
              valueDecimals: 3
            },
            marker: { enabled: false },
            color: this.currentColors[THEME_COLORS.BLUE_LIGHT]
          }
        ];
        this.setConsumptionChartsOptions();
      }
      this.SessionDataService.sendDisableSpinner(false);
    });
  }

  setConsumptionHighchartsOptions(): void {
    let highchartsOptions: any = this.GraphOptionsService.getDefaultHighchartsOptions(
      this.translate.instant("consumption")
    );
    highchartsOptions.exporting.enabled = false;
    highchartsOptions.plotOptions.series.marker.enabled = false;
    this.consumptionHighchartsOptions = highchartsOptions;
  }

  setConsumptionChartsOptions(): void {
    let chartOptions: object = JSON.parse(JSON.stringify(GRAPH_CONFIG.default.chartOptions));
    delete chartOptions["chart"]["navigatorOptions"];
    chartOptions["chart"]["height"] = window.innerWidth > 980 ? 180 : 50;
    chartOptions["chart"]["type"] = "area";
    chartOptions["rangeSelector"]["enabled"] = false;
    chartOptions["navigator"]["enabled"] = false;
    chartOptions["legend"]["enabled"] = false;
    chartOptions["yAxis"][0]["visible"] = false;
    chartOptions["yAxis"][0]["startOnTick"] = false;
    chartOptions["yAxis"][0]["endOnTick"] = false;
    chartOptions["series"] = this.consumptionSeries;
    this.consumptionChartOptions = chartOptions;
  }

  checkMapLayerVisibility(
    mapActiveLayers: string[],
    layerType: string,
    addLayer: boolean,
    okLayer: string,
    noComLayer?: string,
    pendingLayer?: string
  ): string[] {
    if (layerType === "in-network" || layerType === "walk-by") {
      if (!mapActiveLayers.includes(okLayer)) {
        mapActiveLayers.push(okLayer);
      } else if (mapActiveLayers.includes(okLayer) && addLayer) {
        mapActiveLayers.splice(
          mapActiveLayers.findIndex(layer => layer === okLayer),
          1
        );
      }
    } else if (layerType === "no-communication") {
      if (!mapActiveLayers.includes(noComLayer)) {
        mapActiveLayers.push(noComLayer);
      } else if (mapActiveLayers.includes(noComLayer) && addLayer) {
        mapActiveLayers.splice(
          mapActiveLayers.findIndex(layer => layer === noComLayer),
          1
        );
      }
    } else if (layerType === "pending") {
      if (!mapActiveLayers.includes(pendingLayer)) {
        mapActiveLayers.push(pendingLayer);
      } else if (mapActiveLayers.includes(pendingLayer) && addLayer) {
        mapActiveLayers.splice(
          mapActiveLayers.findIndex(layer => layer === pendingLayer),
          1
        );
      }
    }
    return mapActiveLayers;
  }

  updateMapVisibility(deviceType: string, layerType: string, addLayer?: boolean): void {
    let mapActiveLayers = addLayer ? [...this.mapController.mapComponent.getMapActiveLayers()] : [];
    switch (deviceType) {
      case "meters":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.OK,
          MAP_LAYERS.NO_COMUNICA,
          MAP_LAYERS.NO_ASIGNADO
        );
        break;
      case "mbus":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.MBUS_COMUNICA,
          MAP_LAYERS.MBUS
        );
        break;
      case "walkBy":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.WALK_BY
        );
        break;
      case "wavenis":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.WAVENIS,
          MAP_LAYERS.WAVENIS
        );
        break;
      case "external":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.OK,
          MAP_LAYERS.NO_COMUNICA
        );
        break;
      case "externalWithoutCom":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.NO_LORA_NO_COM,
          MAP_LAYERS.NO_LORA_NO_COM
        );
        break;
      case "nbiotUdp":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.NBIOT_UDP_OK,
          MAP_LAYERS.NBIOT_UDP_NO_COMUNICA
        );
        break;
      case "nbiot":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.NBIOT,
          MAP_LAYERS.NBIOT
        );
        break;
      case "concentrators":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.CONCENTRATOR_OK,
          MAP_LAYERS.CONCENTRATOR_NO_COMUNICA,
          MAP_LAYERS.CONCENTRATOR_NO_ASIGNADO
        );
        break;
      case "une":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.UNE_OK,
          MAP_LAYERS.UNE_NO_COMUNICA
        );
        break;
      case "lnsNetmore":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.LNS_NETMORE_OK,
          MAP_LAYERS.LNS_NETMORE_NO_COMUNICA
        );
        break;
      case "tpeNetmore":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.TPE_NETMORE_OK,
          MAP_LAYERS.TPE_NETMORE_NO_COMUNICA
        );
        break;
      case "txn":
        if (!mapActiveLayers.includes(MAP_LAYERS.TXN)) {
          mapActiveLayers.push(MAP_LAYERS.TXN);
        } else if (mapActiveLayers.includes(MAP_LAYERS.TXN) && addLayer) {
          mapActiveLayers.splice(
            mapActiveLayers.findIndex(layer => layer === MAP_LAYERS.TXN),
            1
          );
        }
        break;
      case "gateways":
        if (!mapActiveLayers.includes(MAP_LAYERS.GATEWAYS)) {
          mapActiveLayers.push(MAP_LAYERS.GATEWAYS);
        } else if (mapActiveLayers.includes(MAP_LAYERS.GATEWAYS) && addLayer) {
          mapActiveLayers.splice(
            mapActiveLayers.findIndex(layer => layer === MAP_LAYERS.GATEWAYS),
            1
          );
        }
        break;
      case "sensors":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.SENSOR_OK,
          MAP_LAYERS.SENSOR_NO_COMUNICA
        );
        break;
      case "externalSensors":
        mapActiveLayers = this.checkMapLayerVisibility(
          mapActiveLayers,
          layerType,
          addLayer,
          MAP_LAYERS.EXTERNAL_SENSOR_OK,
          MAP_LAYERS.EXTERNAL_SENSOR_NO_COMUNICA
        );
        break;
      default:
        if (layerType === "in-network") {
          mapActiveLayers = [
            MAP_LAYERS.OK,
            MAP_LAYERS.MBUS_COMUNICA,
            MAP_LAYERS.UNE_OK,
            MAP_LAYERS.CONCENTRATOR_OK,
            MAP_LAYERS.NBIOT,
            MAP_LAYERS.NBIOT_UDP_OK,
            MAP_LAYERS.LNS_NETMORE_OK,
            MAP_LAYERS.TPE_NETMORE_OK,
            MAP_LAYERS.SENSOR_OK
          ];
        } else if (layerType === "no-communication") {
          mapActiveLayers = [
            MAP_LAYERS.UNE_NO_COMUNICA,
            MAP_LAYERS.CONCENTRATOR_NO_COMUNICA,
            MAP_LAYERS.NO_LORA_NO_COM,
            MAP_LAYERS.NO_COMUNICA,
            MAP_LAYERS.WAVENIS,
            MAP_LAYERS.MBUS,
            MAP_LAYERS.NBIOT_UDP_NO_COMUNICA,
            MAP_LAYERS.SENSOR_NO_COMUNICA,
            MAP_LAYERS.LNS_NETMORE_NO_COMUNICA,
            MAP_LAYERS.TPE_NETMORE_NO_COMUNICA
          ];
        } else if (layerType === "pending") {
          mapActiveLayers = [MAP_LAYERS.NO_ASIGNADO, MAP_LAYERS.CONCENTRATOR_NO_ASIGNADO];
        }
        break;
    }

    if (this.mapController.agrupationPolygonActive) {
      this.mapController.hideAgrupationPolygon();
    }
    setTimeout(() => (this.mapActiveLayers = mapActiveLayers), 0);
  }

  loadKpisData(): void {
    this.NetworkStateController.getKpisCards(this.currentAgrupation.id).subscribe(response => {
      if (response["code"] === 0 && response["body"]) {
        let KpisCardsData = response["body"];
        KpisCardsData.timestampParsed = this.DateParser.parseDate(
          KpisCardsData.timestamp,
          untracked(() => this.SessionDataService.dateFormat())
        );
        this.KpisCardsData = KpisCardsData;
      }
    });
  }

  goToKpis(): void {
    if (this.currentAgrupation.virtual) {
      this.router.navigate(["/estado-red/kpis"]);
    } else {
      this.router.navigate(["/estado-red/kpis"], {
        state: { data: { agrupationId: this.currentAgrupation.id } }
      });
    }
  }

  recalculateConsumption(): void {
    this.DataAnalysisController.recalculateConsumptions(
      untracked(() => this.SessionDataService.entity()).id
    ).subscribe(response => {
      if (response["code"] === 0) {
        this.ToastService.fireToast("success", this.translate.instant("recalculate-data-ongoing"));
      }
    });
  }
}
